import { BarElement, CategoryScale, Chart as ChartJS, ChartData, Filler, Legend, LineElement, LinearScale, PointElement, Title, Tooltip, ChartOptions } from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels'

import annotationPlugin from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";
import { useColorTheme } from "services/theme.service";
import { colorWithTransparency } from "utils/color.util";
import { View } from "components/view/view.component";
import { Text } from "components/text/text.component";
import style from './occupation.count.graphic.component.module.css'

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin
);

type OccupationCountGraphicComponentProps = {
    labels?: string[],
    restauranteValues?: (number | undefined)[],
    salaDeMarmitasValues?: (number | undefined)[],
    showLegends?: boolean,
    title?: string,
    max?: number,
    showReferenceLines?: boolean
    showReferenceLinesLegends?: boolean
    showMaxReferenceLine?: boolean
    referenceValue?: number
    disableDatalabels?: boolean
    hideSalaDeMarmitas?: boolean
}

export function OccupationCountGraphicComponent({labels, restauranteValues, salaDeMarmitasValues, showLegends, title, max, showReferenceLines, showMaxReferenceLine, showReferenceLinesLegends, referenceValue, disableDatalabels, hideSalaDeMarmitas=false}: OccupationCountGraphicComponentProps){
    const { COLOR } = useColorTheme()

    const restauranteMaxValue = Math.max(...(restauranteValues?.filter(v => v !== undefined) ?? []) as number[])
    const salaDeMarmitasMaxValue = Math.max(...(salaDeMarmitasValues?.filter(v => v !== undefined) ?? []) as number[])
    const maxValue = Math.max(restauranteMaxValue, salaDeMarmitasMaxValue, ((showReferenceLines && referenceValue) ? referenceValue : 0))
    const suggestedMax = Math.max(max ?? 0 , maxValue * 1.1)

    const referenceValue100 = referenceValue ? referenceValue : 100
    const referenceValue80 = referenceValue ? referenceValue * 0.8 : 80
    const referenceValue70 = referenceValue ? referenceValue * 0.7 : 70

    const lineChartIndicatorData: ChartData<'line', (number | undefined)[], string > = {
        labels: labels ?? [],
        datasets: [
            {
                label: 'Restaurante',
                data: restauranteValues ?? [],
                pointRadius: 2,
                borderColor: COLOR.blue_primary_color,
                backgroundColor: colorWithTransparency(COLOR.blue_primary_color, 0.3),
                datalabels: {
                    display: !disableDatalabels
                }
            },
            ...(!hideSalaDeMarmitas ? [{
                label: 'Sala de Marmitas',
                data: salaDeMarmitasValues ?? [],
                pointRadius: 2,
                borderColor: COLOR.red_primary_color,
                backgroundColor: colorWithTransparency(COLOR.red_primary_color, 0.3),
                datalabels: {
                    display: !disableDatalabels
                }
            }] : []),
            {
                label: 'Ocupação 100%',
                data: (showReferenceLines || showMaxReferenceLine) ? new Array(labels?.length ?? 0).fill(referenceValue100) : [],
                borderColor: colorWithTransparency(COLOR.red_primary_color, 0.7),
                backgroundColor: colorWithTransparency(COLOR.red_primary_color, 0.7),
                pointRadius: 0,
                pointHoverRadius: 0,
                borderWidth: 1.5,
                datalabels: {
                    display: false
                }
            },
            {
                label: 'Ocupação 80%',
                data: showReferenceLines ? new Array(labels?.length ?? 0).fill(referenceValue80) : [],
                borderColor: colorWithTransparency(COLOR.yellow_primary_color, 0.7),
                backgroundColor: colorWithTransparency(COLOR.yellow_primary_color, 0.7),
                pointRadius: 0,
                pointHoverRadius: 0,
                borderWidth: 1.5,
                datalabels: {
                    display: false
                }
            },
            {
                label: 'Ocupação 70%',
                data: showReferenceLines ? new Array(labels?.length ?? 0).fill(referenceValue70) : [],
                borderColor: colorWithTransparency(COLOR.green_primary_color, 0.7),
                backgroundColor: colorWithTransparency(COLOR.green_primary_color, 0.7),
                pointRadius: 0,
                pointHoverRadius: 0,
                borderWidth: 1.5,
                datalabels: {
                    display: false
                }
            }
        ]
    }

    const options: ChartOptions< 'bar' | 'line' > = {
        responsive: true,
        maintainAspectRatio: false,
        plugins:{
            datalabels: {
                anchor: 'end',
                align: 'end',
                font: { weight: 'bold'},
                // formatter: function (value: any) {
                //     return parseFloat(value.toFixed(0)).toLocaleString('pt-BR').concat((min && value < min )|| (max && value > max) ? ' 🚩' : '') // Display the actual data value
                // },
                // color: (context) => {
                //     const value = context.dataset.data[context.dataIndex] as number
                //     return '#111827'
                // }
            },
            legend:{
                display: false,
            },
            annotation: {
                // annotations: annotations
            },
            
        },
        layout:{
            padding: {
                // top: (type === 'line') ? undefined : 20,
            }
        },
        scales:{
            y:{
                position: 'left',
                beginAtZero: true,
                suggestedMax: suggestedMax,
                // max: suggestedMax,
                // ticks: { color: '#111827' },
                ticks: { 
                    color: COLOR.text_color,
                    callback: function(value) {
                        if(max && (value as number) > max) return '';
                        if(value === referenceValue100) return `${Math.round(value as number)}`;
                        if(value === referenceValue80) return `${Math.round(value as number)}`;
                        if(value === referenceValue70) return `${Math.round(value as number)}`;
                        return Math.round(value as number);
                    }
                },
                afterBuildTicks: function(axis) {
                    if (showReferenceLines && referenceValue) {
                        const ticks = axis.ticks.map(t => t.value);
                        const valuesToAdd = [referenceValue100, referenceValue80, referenceValue70];
                        
                        valuesToAdd.forEach(value => {
                            if (!ticks.includes(value)) {
                                axis.ticks.push({ value: value });
                            }
                        });
                        
                        axis.ticks.sort((a, b) => a.value - b.value);
                    }
                },
                grid: { color: colorWithTransparency(COLOR.text_color, .2), display: true}
            },
            y1: {
                position: 'right',
                beginAtZero: true,
                suggestedMax: suggestedMax,
                display: showReferenceLines,
                ticks: { 
                    color: COLOR.text_color,
                    callback: function(value) {
                        if(value === referenceValue100) return '100%';
                        if(value === referenceValue80) return '80%';
                        if(value === referenceValue70) return '70%';
                        return '';
                    }
                },
                afterBuildTicks: function(axis) {
                    if (showReferenceLines && referenceValue) {
                        axis.ticks = [
                            { value: referenceValue70 },
                            { value: referenceValue80 },
                            { value: referenceValue100 }
                        ];
                    }
                },
                grid: { display: false }
            },
            x: {
                ticks: {
                    color: COLOR.text_color,
                },
                grid: { display: false }
            }
        }
    }
    return <View className={style.GraphicComponent}>
        { title && <Text type="h3">{title}</Text> }
        <View className={style.GraphicContent}>
            <Line
                options= {options}
                data={lineChartIndicatorData}
            />
        </View>
        <View className={style.LegendContent} hide={!showLegends}>
            <View className={style.Legend}> 
                <View className={style.LegendColorBox} style={{backgroundColor: COLOR.blue_primary_color}}> </View>
                <Text>Restaurante</Text>
            </View>
            {!hideSalaDeMarmitas && <View className={style.Legend}> 
                <View className={style.LegendColorBox} style={{backgroundColor: COLOR.red_primary_color}}> </View>
                <Text>Sala de Marmitas</Text>
            </View>}
        </View>
        <View className={style.LegendContent} hide={!showReferenceLinesLegends}>
            <View className={style.Legend}> 
                <View className={style.LegendColorBox} style={{backgroundColor: COLOR.red_primary_color, height: '2px'}}> </View>
                <Text>100% da ocupação do restaurante</Text>
            </View>
            <View className={style.Legend}> 
                <View className={style.LegendColorBox} style={{backgroundColor: COLOR.yellow_primary_color, height: '2px'}}> </View>
                <Text>80% da ocupação do restaurante</Text>
            </View>
            <View className={style.Legend}> 
                <View className={style.LegendColorBox} style={{backgroundColor: COLOR.green_primary_color, height: '2px'}}> </View>
                <Text>70% da ocupação do restaurante</Text>
            </View>
        </View>
    </View>
}